import type { CompCriteria, RowVerdict } from "./compCriteria";
import type { CandidateComp } from "./types";

/**
 * FILTERING THE HELD SET IN THE BROWSER (comps-ux, 2026-10-25).
 *
 * THE PRINCIPLE. A widening done in the browser is free; a widening on the server costs an MLS round
 * trip. The server's cascade already fetched a set of MLS sales (one pull, one envelope: today's
 * radius and months), kept the rows that met the criteria, and sent back the rest as `held_back`.
 * The panel holds both. Changing a control re-filters what is held, instantly, with no request.
 * Only "Refresh from MLS" asks the server again.
 *
 * THE FILTER IS THE SERVER'S. `comp_search._checks` is the reference and this file mirrors it test
 * for test (`compHeldFilter.test.ts`): the same inclusive bounds, the same months-between rule, the
 * same "a record that does not state the field is kept and counted, never a pass and never a drop".
 * Nothing here ranks, selects or prices; it only chooses which held sales are listed.
 *
 * WIDENING IS NEVER SILENT. When fewer than `MIN_OPTIONS` sales match, `widenWithin` loosens living
 * area (one 100 sq ft step) and then distance (one 0.25 mi step), alternating, until it reaches
 * the minimum or has nothing left to loosen, and returns the sentence that says exactly what moved.
 * Months, year, beds, baths, subdivision and type are the agent's explicit choices and are never
 * widened for them. Living area is never widened past the last choice (±1,000 sq ft): "Any" is the
 * agent's call.
 */

export const MIN_OPTIONS = 12;
export const GLA_STEP = 100;
export const GLA_CAP = 1000;
export const RADIUS_STEP = 0.25;
export const RADIUS_CAP = 3;

export const SQFT_PER_ACRE = 43560;

export interface HeldSubject {
  gla: number | null;
  /** The subject's lot in square feet (R2 resolves it), or null: the lot filter is then not applied. */
  lot?: number | null;
  year: number | null;
  subdivision: string | null;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const normName = (v: unknown) => String(v ?? "").toUpperCase().replace(/[.,]/g, " ").split(/\s+/).filter(Boolean).join(" ");

/** `YYYY-MM-DD` or `YYYY-MM`, as `comp_search._sale_date` reads it; anything else is unknown. */
function saleMonths(v: unknown, today: Date): number | null {
  const s = String(v ?? "").trim();
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - mo);
}

function baths(r: CandidateComp): number | null {
  const full = num(r.full_baths);
  if (full !== null) return full + (num(r.half_baths) ?? 0) / 2;
  return num((r as unknown as Record<string, unknown>).baths);
}

/** A comp's lot, as `comp_search.comp_lot` reads it: stated square feet, else acres x 43,560 (computed),
 * else null. Zero is never a lot. */
export function compLot(r: CandidateComp): { sf: number | null; computed: boolean } {
  const rec = r as unknown as Record<string, unknown>;
  const sf = num(rec.lot_sf);
  if (sf !== null && sf > 0) return { sf, computed: false };
  if (Array.isArray(rec.flags) && rec.flags.includes("lot_equals_living_area")) return { sf: null, computed: false };
  const ac = num(rec.lot_acres) ?? num(rec.acres);
  if (ac !== null && ac > 0) return { sf: ac * SQFT_PER_ACRE, computed: true };
  return { sf: null, computed: false };
}

/** A held sale's site in SQUARE FEET for the site range. A multi-parcel sale uses the server's
 * combined `_site_acres` (the client never adds parcels: I-1); a multi-parcel sale without it, or any
 * sale with no stated lot, states no site (null: kept and labeled, never dropped). */
function heldSite(r: CandidateComp): number | null {
  const rec = r as unknown as Record<string, unknown>;
  const combined = num(rec._site_acres);
  if (combined !== null && combined > 0) return combined * SQFT_PER_ACRE;
  if (Array.isArray(rec.parcels) && rec.parcels.length >= 2) return null;
  return compLot(r).sf;
}

/** |v - base| <= pct% of base, inclusive, without rounding a percentage: 10.00% in, 10.01% out. */
const withinPct = (v: number, base: number, pct: number) => Math.abs(v - base) * 100 <= base * pct + 1e-9;

const HOME_TYPES = new Set(["single_family", "townhome", "condo"]);

/** `true` / `false` / `null` (the record does not state it) for every criterion in force, with the
 * label the server uses for the "kept because the record does not say" footnote. */
function checks(c: CompCriteria, subject: HeldSubject, today: Date): [string, (r: CandidateComp) => boolean | null][] {
  const out: [string, (r: CandidateComp) => boolean | null][] = [];
  const t = (v: number | null, ok: (n: number) => boolean) => (v === null ? null : ok(v));
  out.push(["distance", (r) => t(num(r.distance), (v) => v <= c.radius_mi + 1e-9)]);
  out.push(["sale date", (r) => t(saleMonths(r.sale_date, today), (v) => v <= c.months)]);
  const sGla = subject.gla;
  if (c.gla_band_sqft != null && sGla) {
    const band = c.gla_band_sqft;
    out.push(["living area", (r) => t(num(r.gla), (v) => Math.abs(v - sGla) <= band + 1e-9)]);
  } else if (c.gla_band_pct != null && sGla) {
    const lo = sGla * (1 - c.gla_band_pct / 100);
    const hi = sGla * (1 + c.gla_band_pct / 100);
    out.push(["living area", (r) => t(num(r.gla), (v) => v >= lo && v <= hi)]);
  }
  const sLot = subject.lot ?? null;
  if (c.lot_band_pct != null && sLot && sLot > 0) {
    const lp = c.lot_band_pct;
    out.push(["lot size", (r) => t(compLot(r).sf, (v) => withinPct(v, sLot, lp))]);
  }
  if (c.hide_unknown_lot) out.push(["lot size", (r) => compLot(r).sf !== null]);
  if (c.min_beds !== null) {
    const mb = c.min_beds;
    out.push(["bedrooms", (r) => t(num(r.beds), (v) => v >= mb)]);
  }
  if (c.min_baths !== null) {
    const mb = c.min_baths;
    out.push(["bathrooms", (r) => t(baths(r), (v) => v >= mb)]);
  }
  const sYear = subject.year;
  if (c.year_band_years !== null && sYear) {
    const yb = c.year_band_years;
    out.push(["year built", (r) => t(num(r.year), (v) => Math.abs(v - sYear) <= yb)]);
  }
  if (c.acreage_mode && c.site_acres_min != null && c.site_acres_max != null) {
    const lo = c.site_acres_min * SQFT_PER_ACRE;
    const hi = c.site_acres_max * SQFT_PER_ACRE;
    out.push(["site size", (r) => t(heldSite(r), (v) => v >= lo - 1e-6 && v <= hi + 1e-6)]);
  }
  const sSub = normName(subject.subdivision);
  if (c.same_subdivision && sSub) {
    out.push(["subdivision", (r) => (normName(r.subdivision) === "" ? null : normName(r.subdivision) === sSub)]);
  }
  return out;
}

function typeVerdict(c: CompCriteria, r: CandidateComp): boolean | null {
  if (!c.home_types?.length) return true;
  const t = (r as unknown as Record<string, unknown>).home_type;
  if (typeof t !== "string" || !HOME_TYPES.has(t)) return null;
  return c.home_types.includes(t as never);
}

/** One row against the criteria, as the server's `evaluate` reports it. */
export function verdictFor(r: CandidateComp, c: CompCriteria, subject: HeldSubject, today: Date = new Date()): RowVerdict {
  const v = checks(c, subject, today).map(([label, p]) => [label, p(r)] as const);
  v.push(["property type", typeVerdict(c, r)]);
  return { matches: !v.some(([, x]) => x === false), unchecked: v.filter(([, x]) => x === null).map(([l]) => l) };
}

/** The held rows that meet the criteria, in the order they are held. A row that states no property
 * type (when types are chosen) is kept and marked `_home_type_unstated`, as the server does. */
export function filterHeld(pool: readonly CandidateComp[], c: CompCriteria, subject: HeldSubject,
                           today: Date = new Date()): CandidateComp[] {
  const cs = checks(c, subject, today);
  const lotInForce = c.lot_band_pct != null && !!subject.lot && subject.lot > 0;
  const siteInForce = !!c.acreage_mode && c.site_acres_min != null && c.site_acres_max != null;
  const out: CandidateComp[] = [];
  for (const r of pool) {
    const type = typeVerdict(c, r);
    if (type === false || cs.some(([, p]) => p(r) === false)) continue;
    // R6: a row with no lot on record is kept, labeled, and (range in force) not a match.
    const lotFlags = { _lot_unmatched: lotInForce && compLot(r).sf === null ? true : undefined,
                       _site_unlisted: siteInForce && heldSite(r) === null ? true : undefined };
    out.push(type === null ? { ...r, ...lotFlags, _home_type_unstated: true } : { ...r, ...lotFlags, _home_type_unstated: undefined });
  }
  return out;
}

export const matchedCount = (rows: readonly CandidateComp[]) => rows.filter((r) => !r._home_type_unstated && !r._lot_unmatched && !r._site_unlisted).length;

/** How many kept rows state no site while the site range is in force: kept and labeled, counted apart from matched. */
export const siteUnlistedCount = (rows: readonly CandidateComp[]) => rows.filter((r) => !!r._site_unlisted).length;

/** How many of these comps have no lot size on record ("N comps have no lot size on record"). */
export const unknownLotCount = (rows: readonly CandidateComp[]) => rows.filter((r) => compLot(r).sf === null).length;

const sqft = (n: number) => n.toLocaleString("en-US");
const mi = (n: number) => `${Number(n.toFixed(2))} mi`;

export interface WidenResult {
  /** The criteria actually applied: the agent's, loosened only as far as it took. */
  used: CompCriteria;
  rows: CandidateComp[];
  /** Exactly what moved ("Widened to ±300 sq ft and 0.75 mi to reach 12 options."), or null. */
  widenedNote: string | null;
  /** Set when even the widest reach left fewer than `min` options. Never silent. */
  shortNote: string | null;
}

/** The farthest any held row sits, rounded up to the next step: past it a wider radius adds nothing. */
function farthest(pool: readonly CandidateComp[]): number {
  let m = 0;
  for (const r of pool) { const d = num(r.distance); if (d !== null && d > m) m = d; }
  return Math.min(RADIUS_CAP, Math.ceil(m / RADIUS_STEP) * RADIUS_STEP);
}

export function widenWithin(pool: readonly CandidateComp[], chosen: CompCriteria, subject: HeldSubject,
                            today: Date = new Date(), min: number = MIN_OPTIONS): WidenResult {
  let used = chosen;
  let rows = filterHeld(pool, used, subject, today);
  // ACREAGE MODE DOES NOT WIDEN. Its radius, months and ranges are the agent's stated choices, and
  // the screen states no widening step for them, so none is taken. A short list says so.
  if (chosen.acreage_mode) {
    const n = matchedCount(rows);
    return { used, rows, widenedNote: null,
             shortNote: n >= min ? null
               : `Only ${n} ${n === 1 ? "option" : "options"} in the ${pool.length} ${pool.length === 1 ? "sale" : "sales"} held match your ranges. Acreage search does not widen them. Change a range, or use "Refresh from MLS" to look again.` };
  }
  if (matchedCount(rows) >= min) return { used, rows, widenedNote: null, shortNote: null };

  const canSize = chosen.gla_band_sqft != null && !!subject.gla;
  const radiusTop = Math.max(chosen.radius_mi, farthest(pool));
  // The earliest step that gave the most options: a widening that added nothing is not claimed.
  let best = { used, rows };
  const consider = () => {
    rows = filterHeld(pool, used, subject, today);
    if (matchedCount(rows) > matchedCount(best.rows)) best = { used, rows };
    return matchedCount(rows) >= min;
  };
  for (;;) {
    let step = false;
    if (canSize && (used.gla_band_sqft as number) + GLA_STEP <= GLA_CAP) {
      used = { ...used, gla_band_sqft: (used.gla_band_sqft as number) + GLA_STEP };
      step = true;
      if (consider()) break;
    }
    if (used.radius_mi + RADIUS_STEP <= radiusTop + 1e-9) {
      used = { ...used, radius_mi: Number((used.radius_mi + RADIUS_STEP).toFixed(2)) };
      step = true;
      if (consider()) break;
    }
    if (!step) break;
  }
  ({ used, rows } = best);
  // ONLY THE STEPS THAT ADDED ROWS ARE CLAIMED. The loop alternates size and distance and keeps every
  // step it took; hand each one back and keep the hand-back when the count does not drop.
  const count = matchedCount(rows);
  if (used.radius_mi !== chosen.radius_mi) {
    const back = { ...used, radius_mi: chosen.radius_mi };
    const r2 = filterHeld(pool, back, subject, today);
    if (matchedCount(r2) >= count) { used = back; rows = r2; }
  }
  if (used.gla_band_sqft !== chosen.gla_band_sqft) {
    const back = { ...used, gla_band_sqft: chosen.gla_band_sqft };
    const r2 = filterHeld(pool, back, subject, today);
    if (matchedCount(r2) >= count) { used = back; rows = r2; }
  }

  const parts: string[] = [];
  if (used.gla_band_sqft !== chosen.gla_band_sqft && used.gla_band_sqft != null) parts.push(`±${sqft(used.gla_band_sqft)} sq ft`);
  if (used.radius_mi !== chosen.radius_mi) parts.push(mi(used.radius_mi));
  const reached = matchedCount(rows) >= min;
  const widenedNote = parts.length
    ? (reached
        ? `Widened to ${parts.join(" and ")} to reach ${min} options.`
        : `Widened to ${parts.join(" and ")}, as far as the sales we hold go.`)
    : null;
  const n = matchedCount(rows);
  const shortNote = reached ? null
    : `Only ${n} ${n === 1 ? "option" : "options"} in the ${pool.length} ${pool.length === 1 ? "sale" : "sales"} held from the last search. Use "Refresh from MLS" to look again.`;
  return { used, rows, widenedNote, shortNote };
}

const hasEdits = (c: CandidateComp) => !!c._raw && Object.keys(c._raw).length > 0;

/**
 * Lay the filtered held set over what is on screen, LOCALLY (the instant twin of `reconcileRerun`).
 *
 *   · Rows the agent brought (uploaded, typed) stay exactly where they are.
 *   · Search results come back in the order they are held (`pool`), each one the agent's own row if
 *     they have one (so a tick or an edit is never replaced by a fresh copy).
 *   · A search result that no longer meets the criteria leaves the list ONLY if the agent has no
 *     stake in it. A ticked or edited one stays, flagged `_outside_criteria`, and a ticked one is
 *     counted in `keptOutside` so the panel can say so. Nothing the agent touched ever vanishes.
 */
export function applyHeld(
  prev: readonly CandidateComp[],
  pool: readonly CandidateComp[],
  wanted: readonly CandidateComp[],
  isSearchRow: (c: CandidateComp) => boolean,
  key: (c: CandidateComp) => string,
): { comps: CandidateComp[]; keptOutside: number } {
  const own = prev.filter((c) => !isSearchRow(c));
  const held = new Map(prev.filter(isSearchRow).map((c) => [key(c), c]));
  const wantedBy = new Map(wanted.map((c) => [key(c), c]));
  const out: CandidateComp[] = [];
  const seen = new Set<string>();
  let keptOutside = 0;
  const place = (k: string, fresh: CandidateComp | undefined) => {
    if (seen.has(k)) return;
    seen.add(k);
    const mine = held.get(k);
    const w = wantedBy.get(k);
    if (w) {
      out.push(mine ? { ...mine, _outside_criteria: undefined, _home_type_unstated: w._home_type_unstated, _lot_unmatched: w._lot_unmatched, _site_unlisted: w._site_unlisted } : w);
    } else if (mine && (mine._selected || hasEdits(mine))) {
      out.push({ ...mine, _outside_criteria: true });
      if (mine._selected) keptOutside += 1;
    } else if (fresh) {
      /* held, unticked, unedited, outside the criteria: not listed (it stays in the pool) */
    }
  };
  for (const p of pool) place(key(p), p);
  for (const [k] of held) place(k, undefined);        // a row on screen the pool never held: same rule
  return { comps: [...own, ...out], keptOutside };
}

/** The pool the panel filters: search results on screen plus the held-back rows, one row per sale. */
export function mergePool(
  onScreen: readonly CandidateComp[],
  heldBack: readonly CandidateComp[],
  key: (c: CandidateComp) => string,
): CandidateComp[] {
  const seen = new Set<string>();
  const out: CandidateComp[] = [];
  for (const c of [...onScreen, ...heldBack]) {
    const k = key(c);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ ...c, _selected: false, _outside_criteria: undefined, _lead: undefined });
  }
  return out;
}
